function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Link } from "react-router-dom";
import { Card, Pill, Empty, Pending } from "../../components/analytics-kit";
import { num, usd, titleCase } from "./territory-common";
import { usdK, pct0, BandChip, AttainBar, PaceText } from "./exec-kit";
import { addDealerNote, updateDealerNote, deleteDealerNote } from "./sales-api";
import { orderStatusLabel, orderStatusTone, currentUserEmail } from "./sales-quotes-api";
import { QuoteStatus, OPEN_STATUSES } from "../quotes/common";
import { isAdminUser } from "../stores/common";
import { isInProgressRange } from "../stores/common";

/*
 * Cards of the dealer detail page (customer-detail.jsx). Every card is fed by
 * one FAIL-SOFT rail from sales-api.js: `undefined` = still loading (render
 * nothing or a skeleton), `null` = the rail is not there (card hidden, or a calm
 * line where the card must stay). Hover descriptions (title attributes) use the
 * same wording as the cockpit's column headers so one phrase means one thing.
 */

// ---- small helpers -----------------------------------------------------------
export function fmtDay(v) {
  if (!v) return "";
  const s = String(v);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s.slice(0, 10)) && s.length <= 10 ? new Date(s + "T00:00:00") : new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}
export function fmtWhen(v) {
  if (!v) return "";
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return String(v);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric"
  }) + (String(v).length > 10 ? ` ${d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  })}` : "");
}
const plural = (n, one, many) => `${num(n)} ${Number(n) === 1 ? one : many || `${one}s`}`;
// Addresses arrive as a string or as { address1, address2, city, region, postalCode }.
export function addrText(a) {
  if (!a) return "";
  if (typeof a === "string") return a;
  // Feeds often send addresses in capitals; show street and city in normal case, state codes as-is.
  const line = titleCase([a.address1 || a.line1 || a.address || a.street, a.address2 || a.line2 || a.street2].filter(Boolean).join(" "));
  const city = [a.city ? titleCase(a.city) : null, a.region || a.state].filter(Boolean).join(", ");
  return [line, [city, a.postalCode || a.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}
const CADENCE_T = "How long since this dealer last ordered, next to how often it usually orders.";
const STAKE_T = "What this dealer bought in the last twelve months, the yearly sales at stake if it stops ordering.";
const BAND_T = "This dealer compared with how often it usually orders: on track, slowing, overdue or stopped ordering.";
const PACE_T = "Where the window is tracking to land against plan, projected from the days elapsed";

// ---- 1. Health block (inside the Account card) ---------------------------------
export function HealthBlock({
  card,
  range
}) {
  if (card === undefined) return /*#__PURE__*/React.createElement("div", {
    className: "dd-health"
  }, /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, "\u2026"));
  if (!card || !card.health && !card.target) return null;
  const h = card.health || {};
  const inProgress = isInProgressRange(range);
  const days = h.daysSinceOrder;
  const cadence = h.cadenceDays;
  return /*#__PURE__*/React.createElement("div", {
    className: "dd-health"
  }, h.band ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row",
    title: BAND_T
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k"
  }, "Health"), /*#__PURE__*/React.createElement(BandChip, {
    health: h
  })) : null, days != null ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row",
    title: CADENCE_T
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k"
  }, "Last order"), /*#__PURE__*/React.createElement("span", null, plural(days, "day"), " since the last order", cadence ? `, usually every ${plural(cadence, "day")}` : "", h.lastOrder || card.lastOrder ? /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, " \xB7 last ", fmtDay(h.lastOrder || card.lastOrder)) : null)) : null, h.expectedNextOrder ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row",
    title: "When the next order is due if the account keeps its usual cadence"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k"
  }, "Next order"), /*#__PURE__*/React.createElement("span", null, "expected ", fmtDay(h.expectedNextOrder))) : null, h.revenueAtStake ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row",
    title: STAKE_T
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k"
  }, "At stake"), /*#__PURE__*/React.createElement("span", null, usdK(h.revenueAtStake), " a year")) : null, card.target ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row dd-plan"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k",
    title: "Revenue target for the window"
  }, "Plan"), /*#__PURE__*/React.createElement("span", {
    className: "dd-planline"
  }, /*#__PURE__*/React.createElement("span", {
    title: "Revenue target for the window"
  }, usdK(card.target)), /*#__PURE__*/React.createElement("span", {
    title: inProgress ? "Revenue against the share of the target that has elapsed so far" : "Revenue against the target for the window"
  }, /*#__PURE__*/React.createElement(AttainBar, {
    ratio: card.attainment
  })), inProgress && card.pace != null ? /*#__PURE__*/React.createElement("span", {
    title: PACE_T
  }, "pace ", /*#__PURE__*/React.createElement(PaceText, {
    ratio: card.pace
  })) : null)) : null);
}

// ---- 6. Lifetime strip (under the ribbon) ---------------------------------------
export function LifetimeStrip({
  life
}) {
  if (!life) return null; // undefined = loading, null = not available yet
  const tiles = [life.firstOrder ? {
    l: "First order",
    v: fmtDay(life.firstOrder),
    t: "The account's first order ever"
  } : null, life.lastOrder ? {
    l: "Last order",
    v: fmtDay(life.lastOrder),
    t: "The account's most recent order"
  } : null, {
    l: "Orders",
    v: num(life.orders),
    t: "Orders ever placed by this account"
  }, life.units ? {
    l: "Units",
    v: num(life.units),
    t: "Units ever bought by this account"
  } : null, {
    l: "Revenue",
    v: usdK(life.revenue),
    t: "Revenue ever booked at this account"
  }, life.avgOrderValue != null ? {
    l: "Avg order",
    v: usdK(life.avgOrderValue),
    t: "Lifetime revenue divided by lifetime orders"
  } : null, life.avgGapDays != null ? {
    l: "Between orders",
    v: plural(Math.round(life.avgGapDays), "day"),
    t: "Average days between one order and the next, lifetime"
  } : null, life.yearsActive != null ? {
    l: "Years active",
    v: Number(life.yearsActive).toFixed(life.yearsActive >= 10 ? 0 : 1),
    t: "Years between the first order and today"
  } : null].filter(Boolean);
  return /*#__PURE__*/React.createElement("div", {
    className: "dd-life",
    title: "Lifetime figures, every order ever booked at this account"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-life-k"
  }, "Lifetime"), tiles.map(t => /*#__PURE__*/React.createElement("span", {
    className: "dd-life-t",
    key: t.l,
    title: t.t
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, t.l), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, t.v))));
}

// ---- 2. Open business (quotes feature only) -------------------------------------
export function OpenBusinessCard({
  quotes,
  orders,
  span = 6
}) {
  if (quotes === undefined && orders === undefined) return /*#__PURE__*/React.createElement(Card, {
    title: "Open business",
    span: span
  }, /*#__PURE__*/React.createElement(Pending, {
    h: 90
  }));
  if (quotes === null && orders === null) return null;
  const qs = (quotes || []).filter(q => OPEN_STATUSES.includes(q.status));
  const os = (orders || []).filter(o => !["invoiced", "cancelled"].includes(o.status));
  if (qs.length + os.length === 0) return null;
  return /*#__PURE__*/React.createElement(Card, {
    title: "Open business",
    hint: "quotes in flight and order requests not yet invoiced \xB7 click to open",
    span: span
  }, qs.length + os.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, "No open quotes or order requests.") : /*#__PURE__*/React.createElement("div", {
    className: "tblwrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table dd-open"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
    title: "Quote or order request number"
  }, "Number"), /*#__PURE__*/React.createElement("th", {
    title: "Quote title, or the ERP order id once one is assigned"
  }, "What"), /*#__PURE__*/React.createElement("th", {
    title: "Where it stands right now"
  }, "Status"), /*#__PURE__*/React.createElement("th", {
    className: "n",
    title: "Net amount after tier and overrides"
  }, "Net"), /*#__PURE__*/React.createElement("th", {
    title: "Quotes: valid until; order requests: when the status last changed"
  }, "When"))), /*#__PURE__*/React.createElement("tbody", null, qs.map(q => /*#__PURE__*/React.createElement("tr", {
    key: `q${q.id}`,
    className: "clickable"
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Link, {
    to: `/Quotes/${encodeURIComponent(q.id)}`,
    title: "Open this quote"
  }, q.number || q.id), /*#__PURE__*/React.createElement("span", {
    className: "code"
  }, "quote")), /*#__PURE__*/React.createElement("td", null, q.title || /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "untitled")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(QuoteStatus, {
    q: q
  })), /*#__PURE__*/React.createElement("td", {
    className: "n strong"
  }, usd(q.subtotalNet)), /*#__PURE__*/React.createElement("td", null, q.validUntil ? /*#__PURE__*/React.createElement("span", {
    title: "Valid until"
  }, fmtDay(q.validUntil)) : q.sentDate ? /*#__PURE__*/React.createElement("span", {
    title: "Sent"
  }, "sent ", fmtWhen(q.sentDate)) : /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "draft")))), os.map(o => /*#__PURE__*/React.createElement("tr", {
    key: `o${o.id}`,
    className: "clickable"
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Link, {
    to: `/Quotes/orders/${encodeURIComponent(o.id)}`,
    title: "Open this order request"
  }, o.number || o.id), /*#__PURE__*/React.createElement("span", {
    className: "code"
  }, "order request")), /*#__PURE__*/React.createElement("td", null, o.erpOrderId ? /*#__PURE__*/React.createElement("span", {
    title: "ERP order id"
  }, "ERP ", o.erpOrderId) : o.quoteNumber ? `from quote ${o.quoteNumber}` : /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "awaiting ERP")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
    tone: orderStatusTone(o.status)
  }, orderStatusLabel(o.status))), /*#__PURE__*/React.createElement("td", {
    className: "n strong"
  }, usd(o.totalNet)), /*#__PURE__*/React.createElement("td", null, o.statusDate ? fmtWhen(o.statusDate) : /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "no status yet"))))))));
}

// ---- 3. People -------------------------------------------------------------------
function ContactLine({
  email,
  phone
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: "dd-contact"
  }, email ? /*#__PURE__*/React.createElement("span", {
    className: "dd-ct",
    title: "Email on the customer feed"
  }, email, " ", /*#__PURE__*/React.createElement("a", {
    href: `mailto:${email}`,
    className: "wa-rowlink",
    title: "Write an email"
  }, "write")) : null, phone ? /*#__PURE__*/React.createElement("span", {
    className: "dd-ct",
    title: "Phone on the customer feed"
  }, phone, " ", /*#__PURE__*/React.createElement("a", {
    href: `tel:${phone}`,
    className: "wa-rowlink",
    title: "Call"
  }, "call")) : null);
}
export function PeopleCard({
  prof,
  onOpenRep,
  span = 6
}) {
  if (prof === undefined) return /*#__PURE__*/React.createElement(Card, {
    title: "People",
    span: span
  }, /*#__PURE__*/React.createElement(Pending, {
    h: 90
  }));
  if (prof === null) return null;
  const p = prof.profile || {};
  const rep = prof.assignedRep;
  const contacts = prof.contacts || [];
  return /*#__PURE__*/React.createElement(Card, {
    title: "People",
    hint: "contacts from the customer feed \xB7 the assigned rep",
    span: span
  }, p.email || p.phone || p.billTo || p.shipTo ? /*#__PURE__*/React.createElement("div", {
    className: "dd-people-acct"
  }, p.email || p.phone ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k",
    title: "The account's own email and phone from the customer feed"
  }, "Account"), /*#__PURE__*/React.createElement(ContactLine, {
    email: p.email,
    phone: p.phone
  })) : null, p.shipTo && addrText(p.shipTo) ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k",
    title: "Ship-to address on the customer feed"
  }, "Ship to"), /*#__PURE__*/React.createElement("span", null, addrText(p.shipTo))) : null, p.billTo && addrText(p.billTo) && addrText(p.billTo) !== addrText(p.shipTo) ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k",
    title: "Bill-to address on the customer feed"
  }, "Bill to"), /*#__PURE__*/React.createElement("span", null, addrText(p.billTo))) : null) : null, rep ? /*#__PURE__*/React.createElement("div", {
    className: "dd-row dd-rep",
    title: "The rep on the account record"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-k"
  }, "Rep"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("button", {
    className: "rep-state-chip fact-chip",
    onClick: () => onOpenRep && onOpenRep(rep.repCode),
    title: "Open this rep's detail"
  }, rep.name || rep.repCode), /*#__PURE__*/React.createElement(ContactLine, {
    email: rep.email,
    phone: rep.phone
  }))) : null, contacts.length ? /*#__PURE__*/React.createElement("ul", {
    className: "dd-contacts"
  }, contacts.map((c, i) => /*#__PURE__*/React.createElement("li", {
    key: c.personId || `${c.email}|${i}`
  }, /*#__PURE__*/React.createElement("span", {
    className: "dd-cname"
  }, c.personId ? /*#__PURE__*/React.createElement(Link, {
    to: `/Contacts/Detail/${encodeURIComponent(c.personId)}`,
    title: "Open this person's contact record"
  }, c.name) : c.name, c.isPrimary ? /*#__PURE__*/React.createElement(Pill, {
    tone: "info"
  }, "Primary") : null, c.role ? /*#__PURE__*/React.createElement("span", {
    className: "hint",
    title: "Role on the customer feed"
  }, c.role) : null), /*#__PURE__*/React.createElement(ContactLine, {
    email: c.email,
    phone: c.phone
  })))) : /*#__PURE__*/React.createElement(Empty, null, "No contacts on this account yet; they arrive with the customer feed."));
}

// ---- 4. Category mix + peers ----------------------------------------------------
export function CategoryMixCard({
  card,
  peers,
  onOpenProduct,
  span = 6
}) {
  if (card === undefined && peers === undefined) return /*#__PURE__*/React.createElement(Card, {
    title: "Category mix",
    span: span
  }, /*#__PURE__*/React.createElement(Pending, {
    h: 90
  }));
  const cats = card && card.categories || [];
  const hasPeers = Array.isArray(peers) && peers.length > 0;
  if (!cats.length && !hasPeers) return null;
  const mx = Math.max(...cats.map(c => Number(c.share) || 0), 0.0001);
  return /*#__PURE__*/React.createElement(Card, {
    title: "Category mix",
    hint: "share of this account's revenue by category in the window",
    span: span
  }, cats.length ? /*#__PURE__*/React.createElement("div", {
    className: "dd-bars",
    title: "Top categories by revenue in the window"
  }, cats.map((c, i) => /*#__PURE__*/React.createElement("div", {
    className: "dd-bar",
    key: `${c.category}|${i}`
  }, /*#__PURE__*/React.createElement("span", {
    className: "t",
    title: c.category
  }, c.category || "(uncategorized)"), /*#__PURE__*/React.createElement("span", {
    className: "bar"
  }, /*#__PURE__*/React.createElement("i", {
    style: {
      width: `${Math.round((Number(c.share) || 0) / mx * 100)}%`
    }
  })), /*#__PURE__*/React.createElement("span", {
    className: "n",
    title: c.revenue != null ? `${usd(c.revenue)} in the window` : undefined
  }, c.share != null ? pct0(c.share) : c.revenue != null ? usdK(c.revenue) : "")))) : /*#__PURE__*/React.createElement(Empty, null, "No category split for this window."), hasPeers ? /*#__PURE__*/React.createElement("div", {
    className: "dd-peers"
  }, /*#__PURE__*/React.createElement("h3", {
    title: "Products other accounts buy together with this account's top products, which this account has not bought in the window"
  }, "Peers buy, this account doesn't"), /*#__PURE__*/React.createElement("ul", null, peers.map(p => /*#__PURE__*/React.createElement("li", {
    key: p.productId,
    className: onOpenProduct ? "clickable" : "",
    onClick: () => onOpenProduct && onOpenProduct(p),
    title: `Open ${p.title} product detail`
  }, p.imageUrl ? /*#__PURE__*/React.createElement("img", {
    className: "dd-thumb",
    src: p.imageUrl,
    alt: "",
    loading: "lazy"
  }) : /*#__PURE__*/React.createElement("span", {
    className: "dd-thumb dd-thumb-empty"
  }), /*#__PURE__*/React.createElement("span", {
    className: "dd-ptxt"
  }, /*#__PURE__*/React.createElement("span", {
    className: "name"
  }, titleCase(p.title)), /*#__PURE__*/React.createElement("span", {
    className: "code"
  }, p.sku || p.productId, p.category ? ` · ${p.category}` : "")), /*#__PURE__*/React.createElement("span", {
    className: "hint dd-why",
    title: "Why it is suggested"
  }, "bought with ", p.because))))) : null);
}

// ---- 5. Web signal ---------------------------------------------------------------
export function WebSignalCard({
  cell,
  span = 6,
  accountRevenue = null
}) {
  if (cell === undefined) return /*#__PURE__*/React.createElement(Card, {
    title: "Web signal",
    span: span
  }, /*#__PURE__*/React.createElement(Pending, {
    h: 90
  }));
  if (!cell) return null;
  const covered = cell.repCount > 0 || cell.reps && cell.reps.length > 0;
  const reps = cell.reps && cell.reps.length ? cell.reps.length : cell.repCount;
  return /*#__PURE__*/React.createElement(Card, {
    title: "Web signal",
    hint: `ZIP3 ${cell.zip3}${cell.label && cell.label !== cell.zip3 ? ` · ${cell.label}` : ""}${cell.state ? `, ${cell.state}` : ""} · website activity around this account`,
    span: span
  }, /*#__PURE__*/React.createElement("div", {
    className: "dd-signal"
  }, /*#__PURE__*/React.createElement("div", {
    className: "dd-sig",
    title: "Website sessions from this area in the window"
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, "Web sessions"), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, num(cell.demand))), /*#__PURE__*/React.createElement("div", {
    className: "dd-sig",
    title: "Website sessions from this area, ranked 0 to 100 against every other area in the window (100 = the most)"
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, "Demand index"), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, num(cell.demandIndex))), /*#__PURE__*/React.createElement("div", {
    className: "dd-sig",
    title: "Revenue booked at dealers in this area in the window"
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, "Booked"), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, usdK(cell.booked))), /*#__PURE__*/React.createElement("div", {
    className: "dd-sig",
    title: "Dealer accounts with an address in this area"
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, "Dealers"), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, num(cell.dealers))), accountRevenue != null && cell.booked > 0 ? /*#__PURE__*/React.createElement("div", {
    className: "dd-sig",
    title: "This account's revenue as a share of everything booked in its ZIP3 area in the window"
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, "Account share"), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, Math.round(Math.min(1, accountRevenue / cell.booked) * 100), "%")) : null, /*#__PURE__*/React.createElement("div", {
    className: "dd-sig",
    title: "Reps assigned to this ZIP3 in the territory table"
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, "Rep coverage"), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, covered ? /*#__PURE__*/React.createElement(Pill, {
    tone: "success"
  }, plural(reps, "rep"), " assigned") : /*#__PURE__*/React.createElement(Pill, {
    tone: "warning"
  }, "no rep assigned")))));
}

// ---- 7. Timeline + notes ---------------------------------------------------------
const KINDS = {
  order: {
    ic: "O",
    label: "Order"
  },
  quote: {
    ic: "Q",
    label: "Quote"
  },
  order_request: {
    ic: "R",
    label: "Order request"
  },
  note: {
    ic: "N",
    label: "Note"
  },
  journey: {
    ic: "W",
    label: "Website visit"
  }
};
const kindOf = k => KINDS[k] || {
  ic: "•",
  label: String(k || "event").replace(/_/g, " ")
};
function RefLink({
  r
}) {
  if (!r || !r.id) return null;
  if (r.type === "quote") return /*#__PURE__*/React.createElement(Link, {
    className: "wa-rowlink",
    to: `/Quotes/${encodeURIComponent(r.id)}`,
    title: "Open this quote"
  }, "open quote");
  if (r.type === "order_request" || r.type === "order-request" || r.type === "orderrequest") return /*#__PURE__*/React.createElement(Link, {
    className: "wa-rowlink",
    to: `/Quotes/orders/${encodeURIComponent(r.id)}`,
    title: "Open this order request"
  }, "open request");
  return null;
}
export class TimelineCard extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "save", async () => {
      const body = this.state.draft.trim();
      if (!body || this.state.saving) return;
      this.setState({
        saving: true,
        msg: ""
      });
      try {
        const note = await addDealerNote(this.props.accountNumber, body);
        if (note === null) {
          this.setState({
            saving: false,
            msg: "Notes are not available yet; they arrive with the next deploy."
          });
          return;
        }
        if (!note.createdBy) note.createdBy = currentUserEmail();
        this.setState({
          saving: false,
          draft: "",
          notes: [note, ...(this.state.notes || [])]
        });
      } catch (e) {
        this.setState({
          saving: false,
          msg: "The note could not be saved. Try again."
        });
      }
    });
    _defineProperty(this, "onKey", e => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        this.save();
      }
    });
    _defineProperty(this, "startEdit", n => this.setState({
      editingId: n.id,
      editDraft: n.body,
      pendingDelete: "",
      msg: ""
    }));
    _defineProperty(this, "saveEdit", async () => {
      const {
        editingId,
        editDraft
      } = this.state;
      const body = editDraft.trim();
      if (!body || !editingId) return;
      this.setState({
        saving: true
      });
      try {
        const r = await updateDealerNote(this.props.accountNumber, editingId, body);
        if (r === null) {
          this.setState({
            saving: false,
            msg: "Notes are not available yet; they arrive with the next deploy."
          });
          return;
        }
        this.setState({
          saving: false,
          editingId: "",
          notes: (this.state.notes || []).map(n => n.id === editingId ? {
            ...n,
            body,
            modifiedDate: new Date().toISOString()
          } : n)
        });
      } catch (e) {
        this.setState({
          saving: false,
          msg: "The note could not be saved. Try again."
        });
      }
    });
    // Two-click delete: the first click arms the row ("Confirm delete"), the second deletes; it disarms by itself.
    _defineProperty(this, "del", async n => {
      if (this.state.pendingDelete !== n.id) {
        clearTimeout(this._pdTimer);
        this.setState({
          pendingDelete: n.id,
          msg: ""
        });
        this._pdTimer = setTimeout(() => this.setState(s => s.pendingDelete === n.id ? {
          pendingDelete: ""
        } : null), 5000);
        return;
      }
      clearTimeout(this._pdTimer);
      this.setState({
        saving: true,
        pendingDelete: ""
      });
      try {
        const r = await deleteDealerNote(this.props.accountNumber, n.id);
        if (r === null) {
          this.setState({
            saving: false,
            msg: "Notes are not available yet; they arrive with the next deploy."
          });
          return;
        }
        this.setState({
          saving: false,
          notes: (this.state.notes || []).filter(x => x.id !== n.id)
        });
      } catch (e) {
        this.setState({
          saving: false,
          msg: "The note could not be deleted. Try again."
        });
      }
    });
    this.state = {
      notes: props.notes,
      draft: "",
      saving: false,
      editingId: "",
      editDraft: "",
      pendingDelete: "",
      msg: ""
    };
  }
  componentDidUpdate(prev) {
    if (prev.notes !== this.props.notes) this.setState({
      notes: this.props.notes,
      editingId: "",
      pendingDelete: "",
      msg: ""
    });
  }
  componentWillUnmount() {
    clearTimeout(this._pdTimer);
  }
  canEdit(note) {
    const me = String(currentUserEmail() || "").toLowerCase();
    const by = String(note.createdBy || "").toLowerCase();
    return isAdminUser() || me && by && me === by;
  }
  say(msg) {
    this.setState({
      msg
    });
  }
  render() {
    const {
      timeline,
      span = 12
    } = this.props;
    const {
      notes,
      draft,
      saving,
      editingId,
      editDraft,
      pendingDelete,
      msg
    } = this.state;
    const loading = timeline === undefined || notes === undefined;
    // One list: the notes rail is the authority for notes (it carries edit and delete), so the
    // timeline's own note items are dropped; everything else from the timeline keeps its kind.
    const items = [];
    (notes || []).forEach(n => items.push({
      at: n.createdDate,
      kind: "note",
      title: n.body,
      detail: "",
      ref: null,
      note: n
    }));
    (timeline || []).forEach(t => {
      if (t.kind !== "note") items.push(t);
    });
    items.sort((a, b) => String(b.at) > String(a.at) ? 1 : String(b.at) < String(a.at) ? -1 : 0);
    return /*#__PURE__*/React.createElement(Card, {
      title: "Timeline",
      hint: "orders, quotes, order requests, website visits and your notes, newest first",
      span: span
    }, /*#__PURE__*/React.createElement("div", {
      className: "dd-compose"
    }, /*#__PURE__*/React.createElement("label", {
      htmlFor: "dd-note",
      title: "A note for everyone on the sales team who opens this account"
    }, "Add a note"), /*#__PURE__*/React.createElement("textarea", {
      id: "dd-note",
      className: "ec-input dd-note-input",
      rows: 2,
      value: draft,
      placeholder: "What happened, what was promised, what to do next",
      onChange: e => this.setState({
        draft: e.target.value
      }),
      onKeyDown: this.onKey,
      disabled: saving
    }), /*#__PURE__*/React.createElement("div", {
      className: "dd-compose-row"
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: this.save,
      disabled: saving || !draft.trim(),
      title: "Save this note (Cmd+Enter or Ctrl+Enter)"
    }, "Save"), /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, "Cmd+Enter saves"), msg ? /*#__PURE__*/React.createElement("span", {
      className: "hint dd-msg"
    }, msg) : null)), loading ? /*#__PURE__*/React.createElement(Pending, {
      h: 80
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, timeline === null ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Timeline arrives with the next deploy.", notes === null ? " Notes too." : "") : null, items.length ? /*#__PURE__*/React.createElement("ul", {
      className: "dd-timeline"
    }, items.map((it, i) => {
      const k = kindOf(it.kind);
      const n = it.note;
      const editing = n && editingId === n.id;
      return /*#__PURE__*/React.createElement("li", {
        key: `${it.kind}|${it.ref && it.ref.id || n && n.id || i}|${it.at}`,
        className: `dd-ev dd-ev-${it.kind}`
      }, /*#__PURE__*/React.createElement("span", {
        className: "dd-ic",
        title: k.label
      }, k.ic), /*#__PURE__*/React.createElement("span", {
        className: "dd-evbody"
      }, /*#__PURE__*/React.createElement("span", {
        className: "dd-evhead"
      }, /*#__PURE__*/React.createElement("span", {
        className: "dd-evwhen",
        title: String(it.at)
      }, fmtWhen(it.at)), /*#__PURE__*/React.createElement("span", {
        className: "dd-evkind"
      }, k.label), n && n.createdBy ? /*#__PURE__*/React.createElement("span", {
        className: "hint",
        title: "Who wrote it"
      }, n.createdBy) : null, n && n.modifiedDate ? /*#__PURE__*/React.createElement("span", {
        className: "hint"
      }, "edited") : null, /*#__PURE__*/React.createElement(RefLink, {
        r: it.ref
      })), editing ? /*#__PURE__*/React.createElement("span", {
        className: "dd-edit"
      }, /*#__PURE__*/React.createElement("textarea", {
        className: "ec-input dd-note-input",
        rows: 2,
        value: editDraft,
        onChange: e => this.setState({
          editDraft: e.target.value
        }),
        onKeyDown: e => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            this.saveEdit();
          }
        }
      }), /*#__PURE__*/React.createElement("span", {
        className: "dd-compose-row"
      }, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
        onClick: this.saveEdit,
        disabled: saving || !editDraft.trim()
      }, "Save"), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-flat",
        onClick: () => this.setState({
          editingId: ""
        })
      }, "Cancel"))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: "dd-evtitle"
      }, it.title), it.detail ? /*#__PURE__*/React.createElement("span", {
        className: "dd-evdetail"
      }, it.detail) : null), n && !editing && this.canEdit(n) ? /*#__PURE__*/React.createElement("span", {
        className: "dd-evacts"
      }, /*#__PURE__*/React.createElement("button", {
        className: "wa-rowlink dd-link",
        onClick: () => this.startEdit(n),
        title: "Change this note"
      }, "Edit"), /*#__PURE__*/React.createElement("button", {
        className: `wa-rowlink dd-link${pendingDelete === n.id ? " danger" : ""}`,
        onClick: () => this.del(n),
        title: pendingDelete === n.id ? "Click again to delete" : "Delete this note (asks once more)"
      }, pendingDelete === n.id ? "Confirm delete" : "Delete")) : null));
    })) : timeline !== null ? /*#__PURE__*/React.createElement(Empty, null, "Nothing on the timeline for this window yet.") : null));
  }
}